// What a box IS, as hardware and as posture: the reported machine specs, the
// hardening report beside them, the enrollment floors those specs are refused
// under, and the gate that matches a required spec against a box's own.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * Static hardware a box advertises at enroll and, since #3765, on every presence
 * beat — arc 101 slice 04. Persisted to `runners.specs` (jsonb, nullable until advertised)
 * so the assigner can weigh a box's size for placement and dashboards can show
 * "8 vCPU · 32 GiB". Every field is best-effort: a box that can't read a value
 * sends `null` (cpu model / disk) rather than omitting the whole advert, so a
 * partial reading still lands. Sizes are bytes (raw `os.totalmem()` etc.), never
 * pre-formatted — the reader renders units.
 *
 * THIS IS A SNAPSHOT, NOT A READING, and the distinction is load-bearing because
 * a stale spec that looks live is worse than a null one. The runner samples it once
 * per process boot (`sampleBoxSpecs`, `apps/runner/src/stats.ts`) and stamps that
 * value on every beat. `runners.specs` has two writers: the enroll flip
 * (`packages/db/src/queries/runners/enrollment.ts`) and the presence reaper's specs
 * half (`updateRunnerSpecs`, `packages/db/src/queries/runners/rollout.ts`), which
 * copies the beat's advert onto the row when they differ. From #2007, which retired
 * the legacy HTTP heartbeat (`POST /v1/runners/heartbeat`) and its query with it,
 * until #3765 the enroll flip was the only one, and a guest resized after
 * enrollment reported its old size forever.
 * The column still goes stale wherever the reaper writes nothing: a box it does not
 * scan as online, and a binary older than the beat field (no advert). Live presence
 * (`BoxStats.freeMemBytes`, the NATS KV beat) stays current, so the two can disagree.
 * Consumers to weigh that against: `ci-assigner.ts` gates placement on
 * `specsSatisfy`, so a stale grown guest reads `no_capacity` and a stale shrunk one
 * is handed a job it will OOM; `update-rollout.ts` reads only `specs.arch`, which a
 * resize cannot change.
 */
export const BoxSpecsSchema = z.object({
  /** CPU architecture (`os.arch()`): `x64` | `arm64` | … */
  arch: z.string().min(1),
  /** Marketing CPU string (`cpus()[0].model`); null when the OS doesn't expose one. */
  cpuModel: z.string().nullable(),
  /** Logical (hyperthread) core count. */
  cpuCores: z.number().int().positive(),
  /** Total RAM in bytes (`os.totalmem()`). */
  memBytes: z.number().int().nonnegative(),
  /** Total root-filesystem size in bytes; null when `statfs` is unavailable. */
  diskBytes: z.number().int().nonnegative().nullable(),
  /**
   * AVAILABLE root-filesystem bytes (`statfs` `bavail * bsize` — the `df`
   * Available column), null when `statfs` is unavailable. Optional at the schema
   * layer (older join scripts + heartbeats predate it); the ENROLL lane requires
   * it (#1024) — see {@link checkBoxEnrollSpecFloors}.
   */
  diskAvailableBytes: z.number().int().nonnegative().nullable().optional(),
});
export type BoxSpecs = z.infer<typeof BoxSpecsSchema>;

/**
 * Ceiling on the exposed-service list a box may report (#622). A box with this
 * many risky listeners on a public address has already told us what we need to
 * know; the cap bounds an untrusted body, and the COUNT is reported separately
 * so a truncated list never reads as the whole truth.
 */
export const BOX_HARDENING_EXPOSED_MAX = 32;

/** Ceiling on one hardening LABEL — an audit string, never a gate. */
export const BOX_HARDENING_LABEL_MAX = 64;

/** `harden_firewall`'s status words, plus `skipped` for `DZ_HARDEN=0` (`scripts/runner/harden.sh`). */
export const BOX_FIREWALL_STATES = ['enabled', 'unavailable', 'skipped'] as const;

/** `harden_docker_ufw`'s status words (`scripts/runner/harden.sh`). */
export const BOX_DOCKER_UFW_STATES = ['applied', 'present', 'skipped'] as const;

/**
 * One risky service the box found listening on a NON-loopback address —
 * `scripts/runner/harden.sh`'s `scan_exposed` row. The classic footgun this
 * exists to surface: a `-p 5432:5432` passwordless Postgres that Docker
 * published straight past UFW to internet scanners.
 *
 * `proc` is harden.sh's best-effort process label (`unknown` when `ss` gave it
 * none) — a name, never an argv, so it carries no command line and no secret.
 */
export const BoxExposedServiceSchema = z.object({
  port: z.number().int().min(1).max(65_535),
  /** The listening address as harden.sh saw it (`0.0.0.0`, `::`, a public IP). */
  address: z.string().trim().min(1).max(BOX_HARDENING_LABEL_MAX),
  proc: z.string().trim().min(1).max(BOX_HARDENING_LABEL_MAX),
});

/**
 * What `scripts/runner/harden.sh` found and did on one box (#622) — the JSON it
 * already writes to `DZ_HARDEN_SUMMARY`, now a typed wire contract so the
 * control plane can audit it. Snake_case: this is the box's own on-disk shape,
 * parsed as-is rather than translated at the edge.
 *
 * **A report, never a gate.** Nothing here decides whether a box may enroll or
 * run work — an under-spec box is `checkBoxEnrollSpecFloors`'s call, and an
 * exposed Postgres is an operator's. A report that fails this schema is still
 * audited, as `hardeningRejected` (the api's `readHardening`), and enrollment
 * proceeds.
 *
 * **`firewall` / `docker_ufw` are closed enums** (#6829), each harden.sh status
 * word verbatim (`harden_firewall`, `harden_docker_ufw`, the `DZ_HARDEN=0`
 * summary). They were bounded free strings, and that let a producer bug pass for
 * a label: harden.sh's `log` wrote to the stdout its status words are captured
 * from, so every report from 2026-10-20 on carried log lines as the label. A
 * bigger cap would have STORED that junk; an enum rejects it loudly. A new word
 * lands here and in harden.sh in the same PR.
 */
export const BoxHardeningSchema = z.object({
  /** False when `DZ_HARDEN=0` — the operator owns the firewall; harden.sh scanned nothing. */
  enabled: z.boolean(),
  /** harden.sh's firewall verdict. */
  firewall: z.enum(BOX_FIREWALL_STATES),
  /** Its docker↔UFW published-port verdict. */
  docker_ufw: z.enum(BOX_DOCKER_UFW_STATES),
  exposed: z.array(BoxExposedServiceSchema).max(BOX_HARDENING_EXPOSED_MAX),
});
export type BoxHardening = z.infer<typeof BoxHardeningSchema>;

/**
 * One bounded, secret-free line describing a hardening report — the log/audit
 * detail. Leads with the EXPOSURE, because that is the fact an operator acts on;
 * a box that hardened cleanly reads as one short clause.
 */
export function describeBoxHardening(hardening: BoxHardening): string {
  if (!hardening.enabled) return 'hardening skipped (DZ_HARDEN=0) — the operator owns the firewall';
  const state = `firewall ${hardening.firewall}, docker_ufw ${hardening.docker_ufw}`;
  if (hardening.exposed.length === 0) return `no risky service exposed; ${state}`;
  const listed = hardening.exposed
    .map((s) => `${s.proc}:${s.port} on ${s.address}`)
    .join(', ')
    .slice(0, 400);
  return `${hardening.exposed.length} risky service(s) exposed [${listed}]; ${state}`;
}

/**
 * Enrollment-lane hardware floors (#1024, raised in epic #4739 slice 6) — the box
 * is untrusted, so an under-spec box is refused a credential at
 * `POST /v1/runners/enroll` rather than discovered at task time. RAM gates TOTAL
 * memory; disk gates AVAILABLE space (a nearly-full disk is the failure mode, not
 * a small one).
 *
 * **7600 MiB, and deliberately NOT 8192.** The floor is "an 8 GB box", and a real
 * 8 GB box does not report 8192 MiB of MemTotal: the sebby-ovh fleet's 8 GB guests
 * report **7751 MiB** (measured 2026-09-22, `awk '/^MemTotal:/{print int($2/1024)}'
 * /proc/meminfo`), the rest being firmware and kernel reservation. An 8192 floor
 * would therefore refuse every 8 GB box on the market. 7600 sits 151 MiB under the
 * smallest measured 8 GB guest — enough margin for a vendor that reserves more,
 * narrow enough that no 4 GB box slips through.
 *
 * **Why 8 GB at all**: the coding CLI runs on the box, with its checkouts, the
 * codegraph index and a language toolchain beside it, and the monorepo gates it
 * spawns peak over 5 GB. A 4 GB box cannot hold that; the run dies as an OOM the
 * customer reads as "the operation was aborted".
 *
 * READ through `BOX_REQUIREMENTS` (`./box-requirements.ts`, plan 102 slice 05) by
 * `apps/api/src/routes/join.ts` (the emitted enrollment-gate block) and
 * `packages/mcp/src/army/box.ts` (`BOX_ADD_NOTE`). MIRRORED, and the copies must move
 * together, only where no import reaches (fetched-standalone bash):
 *   - `scripts/runner/preflight.sh`      `RAM_FLOOR_MB` + the disk check
 *   - `scripts/attach-vm.sh`             `size_memory_posture`'s floor refusal
 *
 * EXISTING boxes are grandfathered: this is an ENROLLMENT check, read once when a
 * box trades its one-time token for a credential, so a fleet box already enrolled
 * under the old floor keeps running. The fleet's available disk currently runs
 * 20-173 GiB (measured 2026-09-22), and the low-disk resource alert is what covers
 * those boxes — not this gate.
 */
export const BOX_ENROLL_RAM_FLOOR_BYTES = 7600 * 1024 * 1024;
/** See {@link BOX_ENROLL_RAM_FLOOR_BYTES}. 40 GiB AVAILABLE, not total. */
export const BOX_ENROLL_DISK_AVAILABLE_FLOOR_BYTES = 40 * 1024 * 1024 * 1024;

const MIB = 1024 * 1024;
const GIB = 1024 * MIB;

/**
 * The enrollment spec gate (#1024). Returns a human-readable rejection detail,
 * or null when the box meets the floors. Missing/null figures REJECT (fail
 * closed) — a caller omitting specs is an untrusted box skipping the gate.
 *
 * Every refusal names the box's OWN figure, the floor, and what the room is for:
 * an operator reading "under floor" with no number cannot tell whether to resize
 * the box or free a directory.
 */
export function checkBoxEnrollSpecFloors(specs: BoxSpecs | undefined): string | null {
  if (specs === undefined) {
    return 'specs required — the box must advertise its hardware (memBytes, diskAvailableBytes); refetch the join script';
  }
  const ramMib = Math.floor(specs.memBytes / MIB);
  if (specs.memBytes < BOX_ENROLL_RAM_FLOOR_BYTES) {
    return `RAM under floor: ${ramMib} MiB < ${BOX_ENROLL_RAM_FLOOR_BYTES / MIB} MiB (an 8 GB box, which reports ~7751 MiB) — the coding CLI, its checkouts, codegraph and the toolchains it runs need the room; upgrade the box and re-enroll`;
  }
  if (specs.diskAvailableBytes == null) {
    return 'diskAvailableBytes required (available, not total) — refetch the join script; older scripts report total disk only';
  }
  const diskGib = Math.floor(specs.diskAvailableBytes / GIB);
  if (specs.diskAvailableBytes < BOX_ENROLL_DISK_AVAILABLE_FLOOR_BYTES) {
    return `available disk under floor: ${diskGib} GiB < ${BOX_ENROLL_DISK_AVAILABLE_FLOOR_BYTES / GIB} GiB available — the coding CLI's checkouts, the codegraph index and the toolchains need the room; free space or enlarge the disk and re-enroll`;
  }
  return null;
}

/**
 * Is a box with these `specs` big enough to run a job that needs at least
 * `minCpus` logical cores and `minMemBytes` of RAM (the sizing `parseCiSizeLabel`
 * resolves from a `developerz-Nvcpu` label)? The requirement is a FLOOR, not a
 * strict minimum — an exact fit passes, one core or one byte under fails. Bytes
 * compare to bytes (`memBytes` ↔ `minMemBytes`), so there is no unit hop between
 * parse and match. Null specs → **false** (fail-closed): a box that never
 * advertised its hardware can't be proven big enough, so a sized job never lands
 * on an unknown box rather than being placed on a possibly-too-small one. Pure —
 * the size gate the CI assigner (`dispatch/ci-assigner.ts`) and its demand read
 * share, so "is this box big enough" is decided one way everywhere.
 */
export function specsSatisfy(
  specs: BoxSpecs | null,
  req: { minCpus: number; minMemBytes: number },
): boolean {
  if (specs === null) return false;
  return specs.cpuCores >= req.minCpus && specs.memBytes >= req.minMemBytes;
}
